/**
 * App shell — modern admin layout: animated sidebar + glassy topbar + animated
 * route transitions. Serves two modes from one codebase (see lib/mode):
 *
 *  - embedded (Shopify admin): App Bridge auth, single store, onboarding wizard.
 *  - portal (standalone site): public landing at "/", magic-link login gate,
 *    multi-store, connect-store empty state, account/profile + store switcher.
 *
 * Route map (portal):
 *   /            → public marketing landing (login-aware CTAs)
 *   /login       → passwordless login
 *   /auth/verify → magic-link landing
 *   /dashboard…  → the authenticated app shell (login + store gates apply)
 *
 * The full structure always renders with placeholder/zero states; pages never go
 * blank when the backend is unreachable.
 */

import { Suspense, lazy, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { Topbar } from './components/Topbar';
import { Skeleton } from './components/ui';
import { useProfile } from './hooks/useApi';
import { useSession, useStores } from './hooks/usePortal';
import { IS_PORTAL } from './lib/mode';

// Lazy-load route pages so the initial bundle stays small.
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Orders = lazy(() => import('./pages/Orders').then((m) => ({ default: m.Orders })));
const NeedsAttention = lazy(() =>
  import('./pages/NeedsAttention').then((m) => ({ default: m.NeedsAttention })),
);
const OrderDetail = lazy(() =>
  import('./pages/OrderDetail').then((m) => ({ default: m.OrderDetail })),
);
const Conversations = lazy(() =>
  import('./pages/Conversations').then((m) => ({ default: m.Conversations })),
);
const ConversationDetail = lazy(() =>
  import('./pages/ConversationDetail').then((m) => ({ default: m.ConversationDetail })),
);
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const Templates = lazy(() => import('./pages/Templates').then((m) => ({ default: m.Templates })));
const Reports = lazy(() => import('./pages/Reports').then((m) => ({ default: m.Reports })));
const Billing = lazy(() => import('./pages/Billing').then((m) => ({ default: m.Billing })));
const Onboarding = lazy(() =>
  import('./pages/Onboarding').then((m) => ({ default: m.Onboarding })),
);
const LaunchDashboard = lazy(() =>
  import('./pages/LaunchDashboard').then((m) => ({ default: m.LaunchDashboard })),
);
const Landing = lazy(() => import('./pages/Landing').then((m) => ({ default: m.Landing })));
const NotFound = lazy(() => import('./pages/NotFound').then((m) => ({ default: m.NotFound })));
const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const AuthVerify = lazy(() =>
  import('./pages/AuthVerify').then((m) => ({ default: m.AuthVerify })),
);
const Profile = lazy(() => import('./pages/Profile').then((m) => ({ default: m.Profile })));
const Portfolio = lazy(() => import('./pages/Portfolio').then((m) => ({ default: m.Portfolio })));
const WinningProducts = lazy(() =>
  import('./pages/WinningProducts').then((m) => ({ default: m.WinningProducts })),
);
const WinningProductDetail = lazy(() =>
  import('./pages/WinningProductDetail').then((m) => ({ default: m.WinningProductDetail })),
);
const ConnectStore = lazy(() =>
  import('./pages/ConnectStore').then((m) => ({ default: m.ConnectStore })),
);
const TrackOrder = lazy(() =>
  import('./pages/TrackOrder').then((m) => ({ default: m.TrackOrder })),
);

function PageFallback() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-40" />
    </div>
  );
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface-muted text-ink">
      <Suspense fallback={null}>{children}</Suspense>
    </div>
  );
}

export function App() {
  const location = useLocation();

  // Public customer-facing branded tracking page (/t/:trackingNumber) — no auth,
  // no portal chrome. Must be reachable in any mode.
  if (location.pathname.startsWith('/t/')) {
    return (
      <Suspense fallback={null}>
        <TrackOrder />
      </Suspense>
    );
  }

  // Public pages reachable in any mode without auth.
  if (location.pathname === '/welcome' || (IS_PORTAL && location.pathname === '/')) {
    return (
      <Suspense fallback={null}>
        <Landing />
      </Suspense>
    );
  }
  if (location.pathname === '/auth/verify') {
    return (
      <Suspense fallback={null}>
        <AuthVerify />
      </Suspense>
    );
  }
  // Landing spot after "Login with Shopify" on the standalone web: the OAuth
  // callback has already set our portal session cookie, so just send them into
  // the dashboard (PortalApp's gates then route to onboarding/connect as needed).
  if (location.pathname === '/installed') {
    return <Navigate to="/dashboard" replace />;
  }
  if (IS_PORTAL && location.pathname === '/login') {
    return (
      <Suspense fallback={null}>
        <Login />
      </Suspense>
    );
  }

  return IS_PORTAL ? <PortalApp /> : <EmbeddedApp />;
}

/**
 * Standalone portal (authenticated area):
 *   login gate → connect-store gate → first-run onboarding gate → shell.
 *
 * Onboarding runs once per store (until `onboarding.completed`); after that the
 * gate never fires again and the seller lands in the dashboard.
 */
function PortalApp() {
  const location = useLocation();
  const { data: user, isLoading: sessionLoading } = useSession();
  const { data: storesData, isLoading: storesLoading } = useStores();
  const { data: profile, isLoading: profileLoading } = useProfile();

  // While we don't yet know auth state, show a light placeholder (not blank).
  if (sessionLoading) return <FullScreen>{<PageFallback />}</FullScreen>;

  // Not signed in → login.
  if (!user) return <Navigate to="/login" replace />;

  // Signed in but no store connected → connect-store (unless already there).
  const hasStore = (storesData?.stores.length ?? 0) > 0;
  if (!storesLoading && !hasStore && location.pathname !== '/stores/connect') {
    return <Navigate to="/stores/connect" replace />;
  }

  // Store connected but setup not finished → first-run onboarding. We only gate
  // once we actually know the profile, so a slow profile fetch doesn't bounce a
  // returning seller through onboarding.
  const needsOnboarding = hasStore && profile && !profile.onboarding?.completed;
  if (!profileLoading && needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  if (location.pathname === '/onboarding') {
    return (
      <FullScreen>
        <Onboarding />
      </FullScreen>
    );
  }

  return <Shell />;
}

/**
 * Embedded (Shopify admin): a minimal surface. Onboarding runs here until setup
 * is finished; after that we show a launch screen that opens the full dashboard
 * in a new tab on our own site (portal mode). We deliberately do NOT render the
 * heavy dashboard inside the admin iframe.
 */
function EmbeddedApp() {
  const location = useLocation();
  const { data: profile } = useProfile();

  const needsOnboarding = profile && !profile.onboarding?.completed;
  if (location.pathname === '/onboarding' || needsOnboarding) {
    return (
      <FullScreen>
        <Onboarding />
      </FullScreen>
    );
  }

  return (
    <FullScreen>
      <LaunchDashboard />
    </FullScreen>
  );
}

/** The shared dashboard shell (sidebar + topbar + routed pages). */
function Shell() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Embedded mode keeps the dashboard at "/"; portal serves it at "/dashboard".
  const homePath = IS_PORTAL ? '/dashboard' : '/';

  return (
    <div className="flex min-h-screen bg-surface-muted text-ink">
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenMobile={() => setMobileOpen(true)} />

        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8">
          {/* No AnimatePresence/route-key here: with lazy pages + Suspense it
              raced the exit animation and left the panel blank until a manual
              refresh. Each page runs its own PageTransition entry animation. */}
          <Suspense fallback={<PageFallback />}>
            <Routes location={location}>
              <Route path={homePath} element={<Dashboard />} />
              <Route path="/attention" element={<NeedsAttention />} />
              <Route path="/winning-products" element={<WinningProducts />} />
              <Route path="/winning-products/:id" element={<WinningProductDetail />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/orders/:id" element={<OrderDetail />} />
              <Route path="/conversations" element={<Conversations />} />
              <Route path="/conversations/:id" element={<ConversationDetail />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/billing" element={<Billing />} />
              {IS_PORTAL ? <Route path="/portfolio" element={<Portfolio />} /> : null}
              {IS_PORTAL ? <Route path="/profile" element={<Profile />} /> : null}
              {IS_PORTAL ? <Route path="/stores/connect" element={<ConnectStore />} /> : null}
              {/* Redirect stale "/" (portal) and onboarding into the dashboard. */}
              {IS_PORTAL ? <Route path="/" element={<Navigate to="/dashboard" replace />} /> : null}
              <Route path="/onboarding" element={<Navigate to={homePath} replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}
